// Imports
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components/macro';
import MultiFieldContainer from '../../../../../../shareables/foundation/front-end/components/forms/multi-field-container';
import { visuallyHiddenLabelStyle } from '../../../../shared-components/visually-hidden-label';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import usePagination from '../../../../../../shareables/foundation/front-end/utils/use-pagination';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { GetPetsResponse } from '../../../../../../shareables/types/api/main/website/page/type/208/pets/get';
import Pet from './pet';


// Styled components
const Container = styled.div`
	hr {
		margin-top: 1.125rem;
		margin-bottom: 1.25rem;
		border: 0;
		border-top: 0.0625rem solid #eee;
	}
`;

const SearchAndPageSizeWrapper = styled(MultiFieldContainer)`
	label {
		${visuallyHiddenLabelStyle}
	}
	
	margin-bottom: 0.8438rem;
`;

const Heading = styled.h3`
	display: block;
	font-size: 1.5rem;
	margin-top: 1.25rem;
	margin-bottom: 0.5313rem;
	font-weight: 500;
	line-height: 1.1;
`;

const AddPet = styled.div`
	font-style: italic;
	text-align: center;
	font-size: 90%;
`;


// Define the accepted props
interface Props {
	pageID: number;
	storybookPets?: GetPetsResponse;
	storybookSearchQuery?: string;
}


// Function component
const PetDirectory: React.FC<Props> = ({ pageID, storybookPets, storybookSearchQuery }) => {
	// Use state
	const [allPets, setAllPets] = useState<GetPetsResponse | null>(storybookPets ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Create search field
	const {
		items: filteredPets,
		setItems: setFilteredPets,
		searchQuery,
		searchField,
		pageSizeField,
		paginationComponent,
	} = usePagination<GetPetsResponse[0]>({
		cacheStorageKey: `pet-directory-${pageID}-pets`,
		pluralDescriptor: 'pets',
		pageSizes: [50, 100, 250, 500],
		searchableKeys: ['name', 'owner.name', 'owner.address'],
		initialItems: storybookPets,
		initialSearchQuery: storybookSearchQuery,
	});
	
	
	// Load pets on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetPetsResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/208/pets`,
				completion: (json) => {
					setAllPets(json.response);
					setFilteredPets(json.response);
				},
			})
		);
	}, [dispatch, pageID, setFilteredPets]);
	
	
	// If loading, display nothing
	if (allPets === null) {
		return null;
	}
	
	
	// Add placeholder to search field
	const searchFieldWithPlaceholder = React.cloneElement(searchField, { placeholder: 'Search pets…' });
	
	
	// Return JSX
	return (
		<Container>
			<SearchAndPageSizeWrapper centerOnMobile grow={1} moreSpaceBetween>
				{searchFieldWithPlaceholder}
				{pageSizeField}
			</SearchAndPageSizeWrapper>
			
			{allPets.length === 0 && <em>No pets yet…</em>}
			
			{searchQuery.length > 0 && allPets.length > 0 && (
				<React.Fragment>
					<Heading>Showing results for “{searchQuery}”</Heading>
					{filteredPets?.length === 0 && <em>No results found…</em>}
				</React.Fragment>
			)}
			
			{filteredPets?.length === 0 && <hr />}
			
			{filteredPets?.map((pet, i) => <Pet key={i} pet={pet} />)}
			
			{paginationComponent}
			
			<AddPet>
				Want to add your pet? Add pets to your account <Link to='/account/account-settings/members'>on this page</Link>!
			</AddPet>
		</Container>
	);
};

export default PetDirectory;
